import { logError, type Page } from '@subaio/utils'

import type { Container } from './Container'
import type { Dependency } from './Dependency'
import type { Params } from './Params'
import { ContainerStatus, type ContainerStore } from './Store'

const cancellationSymbol = Symbol('ContainerCancellation')

export const initializeContainer = async <
	/** Additional props passed to `initialize` and `component` */
	TInitProps extends Record<string, unknown> = Record<string, any>,
>(
	store: ContainerStore,
	container: Container<{}, any, TInitProps>,
	props: TInitProps,
	getDependency: (path: string) => Promise<unknown>,
) => {
	const { page } = store.getState()
	if (!container.dependencies && !container.initialize) {
		store.set({ status: ContainerStatus.Running, data: container.defaults ?? {} })
		return
	}

	store.set({ status: ContainerStatus.Loading, data: container.defaults ?? {} })

	const isCancelled = () => store.getState().page !== page
	const abortIfCancelled = () => {
		if (isCancelled()) {
			throw cancellationSymbol
		}
	}

	try {
		const data = await initializeState(container, page, props, getDependency, abortIfCancelled)
		// If the page has changed during load, ignore this
		if (isCancelled()) return
		store.set({ data, status: ContainerStatus.Running })
	} catch (error) {
		// Was initialization cancelled?
		if (error === cancellationSymbol) return
		logError(error)
		// If the page has changed during load, ignore this
		if (isCancelled()) return
		store.set({ status: ContainerStatus.Errored })
	}
}

export const invalidate = async <
	/** Additional props passed to `initialize` and `component` */
	TInitProps extends Record<string, unknown> = Record<string, any>,
>(
	store: ContainerStore,
	containers: Record<string, Container<{}, any, TInitProps>>,
	props: TInitProps,
	getDependency: (path: string) => Promise<unknown>,
	invalidDependencies: Array<string>,
) => {
	const { status, page } = store.getState()
	if (status === ContainerStatus.Errored || !(page.name in containers)) return
	try {
		const update = await resolveDependencies(containers[page.name], page, props, getDependency, invalidDependencies)
		const { page: newPage, data } = store.getState()
		// If the page has changed during load, ignore this
		if (newPage !== page) return
		store.set({ data: { ...data, ...update } })
	} catch (error) {
		logError(error)
		// Do nothing, since this was just a refresh, the existing data is preferable to an error
	}
}

const initializeState = async <
	/** Additional props passed to `initialize` and `component` */
	TInitProps extends Record<string, unknown> = Record<string, any>,
>(
	container: Container<{}, any, TInitProps>,
	page: Page,
	props: TInitProps,
	getDependency: (path: string) => Promise<unknown>,
	abortIfCancelled: () => void,
) => {
	let delta = {
		...container.defaults,
		...(await resolveDependencies(container, page, props, getDependency)),
	}

	if (container.initialize) {
		const initialProps = await container.initialize({ ...delta, ...page.params, ...props }, abortIfCancelled)
		delta = { ...delta, ...initialProps }
	}

	return delta
}

const resolveDependencies = <
	/** Additional props passed to `initialize` and `component` */
	TInitProps extends Record<string, unknown> = Record<string, any>,
>(
	container: Container<{}, any, TInitProps>,
	page: Page,
	props: TInitProps,
	getDependency: (path: string) => Promise<unknown>,
	invalidDependencies?: Array<string>,
) => {
	if (!container.dependencies) return null

	const params = page.params ?? {}

	return promiseMapValues(container.dependencies, async (resolveDependency) => {
		const { path, transform = (value: any) => value } = prepareDependency(resolveDependency({ ...params, ...props }))

		// Invalidating '/resource/id' should make both '/resource' and '/resource/id/subresource' invalidate
		if (
			!invalidDependencies ||
			invalidDependencies.some((dependency) => dependency.startsWith(path) || path.startsWith(dependency))
		) {
			return getDependency(path).then((value) => transform(value))
		}
		return undefined
	})
}

const zipCompact = <T>(keys: Array<string>, values: Array<T>) =>
	keys.reduce<Record<string, T>>((obj, key, index) => {
		if (values[index] !== undefined) {
			obj[key] = values[index]
		}
		return obj
	}, {})

const promiseMapValues = async <TIn, TOut>(obj: OptionalRecord<string, TIn>, mapper: (value: TIn) => Promise<TOut>) =>
	zipCompact(
		Object.keys(obj),
		await Promise.all(Object.values(obj).map((value) => (value === undefined ? undefined : mapper(value)))),
	)

const prepareDependency = <TParams extends Params>(
	dependency: Dependency<TParams>,
): Exclude<Dependency<TParams>, string> => {
	if (typeof dependency === 'string') {
		return { path: dependency }
	}
	return dependency
}
